import { useEffect, useMemo, useState } from 'react'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import type {
  RecoveryCandidate,
  RecoveryCriteria,
  RecoveryPreview,
  RecoveryTargetPrivacy,
} from '@/api/endpoints/channelRecovery'
import { EXCLUDED_REASON_LABEL } from '@/features/recovery/labels'
import styles from '@/features/recovery/pages/RecoveryPage/RecoveryPage.module.scss'

type Mode = 'bottom_pct' | 'max_views'

const MODE_OPTIONS: ReadonlyArray<SegmentedOption<Mode>> = [
  { value: 'bottom_pct', label: 'Os % de menos views' },
  { value: 'max_views', label: 'Até N views' },
]

const PRIVACY_OPTIONS: ReadonlyArray<SegmentedOption<RecoveryTargetPrivacy>> = [
  { value: 'unlisted', label: 'Não listado' },
  { value: 'private', label: 'Privado' },
]

const PRIVACY_TEXT: Record<RecoveryTargetPrivacy, string> = {
  unlisted: 'não listado',
  private: 'privado',
}

export interface ArchiveWeakConfirm {
  criteria: RecoveryCriteria
  targetPrivacy: RecoveryTargetPrivacy
  videos: { video_id: string; language: string }[]
}

export interface ArchiveWeakSectionProps {
  /** A flag do servidor; ``false`` desabilita o arquivar (a prévia segue). */
  enabled: boolean | undefined
  preview: RecoveryPreview | undefined
  previewPending: boolean
  onPreview: (criteria: RecoveryCriteria) => void
  creating: boolean
  /** Já há um lote ativo no canal: o servidor recusaria outro. */
  hasActiveBatch: boolean
  onConfirm: (input: ArchiveWeakConfirm) => void
}

function keyOf(c: Pick<RecoveryCandidate, 'video_id' | 'language'>): string {
  return `${c.video_id}::${c.language}`
}

function toInt(value: string, fallback: number): number {
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/**
 * "Arquivar vídeos fracos": o critério, a prévia com uma caixa por vídeo e a
 * confirmação explícita, porque a troca é ao vivo no YouTube.
 */
export function ArchiveWeakSection({
  enabled,
  preview,
  previewPending,
  onPreview,
  creating,
  hasActiveBatch,
  onConfirm,
}: ArchiveWeakSectionProps) {
  const [mode, setMode] = useState<Mode>('bottom_pct')
  const [bottomPct, setBottomPct] = useState('25')
  const [maxViews, setMaxViews] = useState('410')
  const [minAge, setMinAge] = useState('7')
  const [maxVideos, setMaxVideos] = useState('50')
  const [keepMin, setKeepMin] = useState('10')
  const [privacy, setPrivacy] = useState<RecoveryTargetPrivacy>('unlisted')
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)

  const criteria: RecoveryCriteria = useMemo(
    () => ({
      mode,
      bottom_pct: Math.min(100, Math.max(1, toInt(bottomPct, 25))),
      max_views: toInt(maxViews, 410),
      min_age_days: toInt(minAge, 7),
      max_videos: Math.min(500, Math.max(1, toInt(maxVideos, 50))),
      keep_min_public: toInt(keepMin, 10),
    }),
    [mode, bottomPct, maxViews, minAge, maxVideos, keepMin],
  )

  // Uma prévia nova marca os vídeos que o critério selecionou.
  useEffect(() => {
    setChecked(new Set((preview?.candidates ?? []).filter((c) => c.selected).map(keyOf)))
  }, [preview])

  const chosen = (preview?.candidates ?? []).filter((c) => checked.has(keyOf(c)))
  const n = chosen.length
  const units = n * 51
  const dailyUnits = preview?.daily_units ?? 800
  const days = n === 0 ? 0 : Math.ceil(n / Math.max(1, Math.floor(dailyUnits / 51)))
  const publicAfter = (preview?.public_count ?? 0) - n
  const disabledReason =
    enabled === false
      ? 'desligado nesta máquina'
      : hasActiveBatch
        ? 'já há um lote ativo neste canal'
        : null

  const toggle = (c: RecoveryCandidate) => {
    setChecked((prev) => {
      const next = new Set(prev)
      const k = keyOf(c)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  return (
    <Section
      title="Arquivar vídeos fracos"
      description="Tira de vista os vídeos de menos views do canal. É um experimento: nada garante que o canal volte a ser recomendado, e vídeo fora de vista pode deixar de contar para a monetização enquanto estiver fora."
    >
      <div className={styles.form}>
        <Segmented<Mode>
          options={MODE_OPTIONS}
          value={mode}
          onValueChange={setMode}
          aria-label="Critério"
        />
        {mode === 'bottom_pct' ? (
          <div className={styles.field}>
            <span>% de menos views</span>
            <Input
              aria-label="% de menos views"
              type="number"
              min={1}
              max={100}
              value={bottomPct}
              onChange={(e) => {
                setBottomPct(e.target.value)
              }}
            />
          </div>
        ) : (
          <div className={styles.field}>
            <span>Até quantas views</span>
            <Input
              aria-label="Até quantas views"
              type="number"
              min={0}
              value={maxViews}
              onChange={(e) => {
                setMaxViews(e.target.value)
              }}
            />
          </div>
        )}
        <div className={styles.field}>
          <span>Idade mínima (dias)</span>
          <Input
            aria-label="Idade mínima (dias)"
            type="number"
            min={0}
            value={minAge}
            onChange={(e) => {
              setMinAge(e.target.value)
            }}
          />
        </div>
        <div className={styles.field}>
          <span>Máximo de vídeos</span>
          <Input
            aria-label="Máximo de vídeos"
            type="number"
            min={1}
            max={500}
            value={maxVideos}
            onChange={(e) => {
              setMaxVideos(e.target.value)
            }}
          />
        </div>
        <div className={styles.field}>
          <span>Mínimo de públicos</span>
          <Input
            aria-label="Mínimo de públicos"
            type="number"
            min={0}
            value={keepMin}
            onChange={(e) => {
              setKeepMin(e.target.value)
            }}
          />
        </div>
        <Segmented<RecoveryTargetPrivacy>
          options={PRIVACY_OPTIONS}
          value={privacy}
          onValueChange={setPrivacy}
          aria-label="Nova visibilidade"
        />
        <Button
          variant="secondary"
          disabled={previewPending}
          onClick={() => {
            onPreview(criteria)
          }}
        >
          {previewPending ? 'Carregando…' : 'Ver prévia'}
        </Button>
      </div>

      {preview ? (
        <>
          <p className={styles.summary}>
            {n} de {preview.candidates.length} vídeos · custo {units} unidades · {days}{' '}
            {days === 1 ? 'dia' : 'dias'} pelo orçamento · {publicAfter} públicos sobram · mediana
            do canal {preview.median_views ?? '—'} views
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th aria-label="Arquivar" />
                  <th>Vídeo</th>
                  <th>Views</th>
                  <th>Idade</th>
                  <th>Fora por</th>
                </tr>
              </thead>
              <tbody>
                {preview.candidates.map((c) => (
                  <tr key={keyOf(c)}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Arquivar ${c.title}`}
                        checked={checked.has(keyOf(c))}
                        disabled={!c.selectable}
                        onChange={() => {
                          toggle(c)
                        }}
                      />
                    </td>
                    <td className={styles.titleCell}>{c.title}</td>
                    <td>{c.views ?? '—'}</td>
                    <td>{c.age_days !== null ? `${String(c.age_days)} d` : '—'}</td>
                    <td>
                      {c.excluded_reason
                        ? (EXCLUDED_REASON_LABEL[c.excluded_reason] ?? c.excluded_reason)
                        : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.actionsRow}>
            <Button
              variant="danger"
              disabled={n === 0 || disabledReason !== null || creating}
              onClick={() => {
                setConfirmOpen(true)
              }}
            >
              Arquivar {n} vídeos
            </Button>
            {disabledReason ? <span className={styles.muted}>{disabledReason}</span> : null}
          </div>
        </>
      ) : null}

      <DialogRoot open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          title={`Arquivar ${String(n)} vídeos?`}
          description={`Eles passam a ${PRIVACY_TEXT[privacy]} no YouTube.`}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">Cancelar</Button>
              </DialogClose>
              <Button
                variant="danger"
                disabled={creating}
                onClick={() => {
                  setConfirmOpen(false)
                  onConfirm({
                    criteria,
                    targetPrivacy: privacy,
                    videos: chosen.map((c) => ({ video_id: c.video_id, language: c.language })),
                  })
                }}
              >
                Confirmar e arquivar {n}
              </Button>
            </>
          }
        >
          <Callout variant="danger" title="É ao vivo no YouTube">
            A troca vale na hora para quem assiste. O lote anda até o orçamento de cota do dia e
            pode ser pausado ou desfeito depois.
          </Callout>
        </DialogContent>
      </DialogRoot>
    </Section>
  )
}
